/**
 * Every refusal the portal can show has a stable machine code.
 *
 * plan.md section 14.1 requires each RPC to return a stable machine error code, and the attack
 * suite asserts on these rather than on prose. The mapping lives in one place so a screen never
 * invents its own wording for a refusal, and so a message can be changed without changing what a
 * test asserts.
 *
 * The submitted value is never part of the message. A refusal says what was refused, not what was
 * sent.
 */

import { browserIsOffline, isTransportFailure } from "./connection";

export const PORTAL_ERROR_MESSAGES: Record<string, string> = {
  PORTAL_UNAUTHENTICATED: "Sessionen har upphört. Logga in igen.",
  PORTAL_FORBIDDEN_TENANT: "Du har inte åtkomst till det här kundkontot.",
  PORTAL_FORBIDDEN_TENANT_WRITE: "Förvaltningen är pausad, så det går inte att skriva just nu.",
  PORTAL_FORBIDDEN_ROLE: "Din roll saknar behörighet för den här åtgärden.",
  PORTAL_FORBIDDEN_ADMIN_REQUIRED: "Åtgärden kräver ett Petrovic Systems-konto.",
  PORTAL_EMPTY_MESSAGE: "Skriv ett meddelande innan du skickar.",
  // Reworded from "Ni har skickat många meddelanden på kort tid. Vänta en stund." on 2026-09-04,
  // when the admin attachment download became the third caller. The old sentence was wrong twice for
  // an operator who had hit the 30-downloads-per-10-minutes ceiling. It said "meddelanden", and
  // nobody had sent one. And it said "ni", which is how this file addresses a client company, so it
  // read to Petrovic as a statement about his own portal, the same defect
  // `PORTAL_NO_CURRENT_MANAGEMENT_PERIOD` was reworded for below.
  //
  // One code gets one sentence, which is what the file's opening rule means, so the fix is a
  // sentence that is true from every side rather than a second entry. It names no action, because
  // the three buckets that raise it count three different ones: messages sent, one-time secrets
  // handled and files downloaded. "Åtgärd" is this file's own word for a thing a caller does, and it
  // is already used by `PORTAL_FORBIDDEN_ADMIN_REQUIRED` and `PORTAL_FORBIDDEN_ROLE`.
  //
  // What it deliberately does not say is how many are left or when the window opens. The counters
  // are per subject and per bucket, and a portal that quotes one of them is telling a caller who is
  // probing exactly how fast to probe.
  PORTAL_RATE_LIMITED: "Åtgärden har gjorts för många gånger på kort tid. Vänta en stund och försök igen.",
  PORTAL_STEP_UP_REQUIRED: "Bekräfta din identitet innan du fortsätter.",
  // `private.consume_step_up_grant` gives this one answer to a grant that is expired, already spent
  // or issued for something else, and the sentence keeps them together rather than guessing which.
  // A fresh code is the fix for all three.
  PORTAL_STEP_UP_INVALID: "Bekräftelsen hann gå ut. Ange en ny kod och bekräfta igen.",
  // Raised by the management gateways when the period is not in the status the transition needs: a
  // cancellation of something that is not active, or a withdrawal of a cancellation that is no
  // longer scheduled. Both mean the screen was showing a state the database has since left.
  PORTAL_INVALID_MANAGEMENT_TRANSITION:
    "Förvaltningen står inte i det läge åtgärden gäller. Ladda om sidan så visar den vad som gäller nu.",
  // Reworded from "Ni har ingen löpande förvaltning i portalen." when `/admin/records` became the
  // second surface this code reaches. `public.record_processor_instruction` raises it at a client
  // about their own account and `public.admin_set_invoice_schedule` raises it at Petrovic about a
  // client he has just picked from a list, so a sentence addressed to "ni" is false on one of the
  // two surfaces: it reads to the operator as a statement about his own portal. This wording is
  // true from both sides and keeps "i portalen", which is the part that separates the portal's
  // record from the relationship. No test asserted the old string.
  PORTAL_NO_CURRENT_MANAGEMENT_PERIOD: "Kundkontot har ingen löpande förvaltning i portalen.",

  /*
   * The seven the management and service gateways have always raised and this file has never
   * carried, added 2026-09-12 when the write half of `/admin/clients` was built.
   *
   * They were harmless while nothing called those gateways. `toPortalFailure` keeps a code only when
   * it has an entry here and collapses everything else into `PORTAL_UNEXPECTED`, so the moment a
   * screen could reach them, all seven would have arrived on Petrovic's screen as "Något gick fel.
   * Försök igen.", which is also what a dropped connection says. A refusal that cannot be told apart
   * from a network failure is the one an operator retries instead of answering.
   *
   * Every one of them names a state rather than a fault, because that is what they are: the row was
   * not in the shape the transition needs. The forms on `/admin/clients` refuse all seven before
   * sending where the register row gives them enough to judge, so these sentences are the answer for
   * the case the browser could not see coming, which is the screen having gone stale.
   */
  PORTAL_CROSS_TENANT_SERVICE:
    "Tjänsten hör till ett annat kundkonto. Ingenting ändrades. Ladda om sidan.",
  // The one that is a missing value rather than a stale screen, and the only one of the seven an
  // operator fixes by going somewhere else first. `admin_activate_management` reads the period's
  // start date off the service's own publiceringsdatum, so a default of today would put every
  // årsdag weeks wrong.
  PORTAL_MISSING_PUBLICATION_DATE:
    "Tjänsten saknar publiceringsdatum, och förvaltningens startdatum läses därifrån. Sätt datumet på tjänsten först.",
  PORTAL_PAUSE_ALREADY_OPEN: "Förvaltningen är redan pausad. Ingenting ändrades.",
  PORTAL_NO_OPEN_PAUSE: "Det finns ingen öppen paus att avsluta. Ingenting ändrades.",
  PORTAL_NO_MANAGEMENT_TRANSITION_DUE:
    "Ingen övergång är aktuell för perioden ännu. Ingenting ändrades.",
  PORTAL_OFFBOARDING_ALREADY_STARTED:
    "En avslutslista är redan öppen för perioden. Ingenting ändrades.",
  PORTAL_MONITOR_LIMIT_REACHED:
    "Tjänsten har redan så många övervakade adresser som taket tillåter. Ingenting lades till.",
  // 2026-09-22, `public.admin_retire_monitor`. Both are an operator's, who reads "ni" as a client.
  PORTAL_UNKNOWN_MONITOR: "Bevakningen finns inte längre. Ladda om sidan.",
  PORTAL_MONITOR_ALREADY_RETIRED: "Bevakningen är redan borttagen. Ingenting ändrades.",
  // 2026-09-22, `public.admin_invite_person`. plan.md section 4: nobody logs in before the handover,
  // and the handover is when management is activated.
  PORTAL_MANAGEMENT_NOT_READY:
    "Förvaltningen är inte aktiverad för kunden, så ingen inbjudan skickades. Aktivera förvaltningen vid överlämningen först.",
  // 2026-09-22, the storage ceiling. A client reads this, so it says what happened and what still
  // works, and it does not blame the file: a file that fits every other rule is refused because
  // the portal's store is full, which is Petrovic's to solve.
  PORTAL_STORAGE_FULL:
    "Lagringsutrymmet för filer i portalen är fullt, så filen laddades inte upp. Petrovic Systems får ett larm om det. Skicka meddelandet utan fil så länge, eller försök igen senare.",
  PORTAL_INVALID_PROCESSOR_INSTRUCTION:
    "Svaret måste vara att bilagorna skickas tillbaka eller raderas.",
  // Migration 20260913000006. The first is the one an operator can meet in ordinary use: an answer
  // that arrives after the end date for a client whose instruction was already given.
  PORTAL_PROCESSOR_INSTRUCTION_ALREADY_GIVEN:
    "Förvaltningen är avslutad och kunden har redan gett ett besked om bilagorna. Det skrivs inte över, eftersom driftjobbet kan ha agerat på det.",
  PORTAL_INVALID_PROCESSOR_INSTRUCTION_CHANNEL:
    "Beskedet måste ha kommit per mejl eller på annat skriftligt sätt.",
  PORTAL_PROCESSOR_INSTRUCTION_NAME_REQUIRED:
    "Skriv vem hos kunden som gav beskedet.",
  PORTAL_UNKNOWN_MEMBERSHIP: "Kontot finns inte längre i det här kundkontot.",
  // The 2026-09-14 security review, F2, F9, F10 and F14. Until then a wrong value reached a check
  // constraint and came back as `23514` carrying the constraint's name and the caller's own row, and
  // this file rendered it as the fallback. The gateways now refuse by name before the write.
  PORTAL_PUSH_ENDPOINT_NOT_ALLOWED:
    "Den här webbläsaren kan inte ta emot aviseringar från portalen. Aviseringar i portalen och per mejl fungerar som vanligt.",
  // Build round 54, 2026-10-31: `register_push_subscription`'s refusal of an endpoint another account
  // registered. `subscribeToPush` drops that subscription and tries once more with a new endpoint, so
  // this is the answer only when the second try is refused as well.
  PORTAL_PUSH_SUBSCRIPTION_CONFLICT:
    "Den här webbläsaren tar redan emot notiser för ett annat konto i portalen. Logga in med det kontot och stäng av notiserna där, eller slå på notiser i en annan webbläsare.",
  PORTAL_INVALID_SECRET_TTL: "Tiden en hemlighet får vänta måste vara mellan en sekund och sju dagar.",
  PORTAL_INVALID_MEMBERSHIP_STATUS: "Det är inte ett giltigt läge för en person.",
  PORTAL_INVALID_MEMBERSHIP_REASON:
    "Skälet får vara högst 200 tecken och skrivas på en rad.",
  PORTAL_INVALID_MEMBERSHIP_TRANSITION:
    "En person som har loggat in kan inte göras till inbjuden igen.",
  PORTAL_INVALID_NOTIFICATION_CATEGORY: "Det finns ingen sådan aviseringstyp.",
  PORTAL_INVALID_LOCALE: "Det språket stöds inte.",
  PORTAL_INVALID_THEME_PREFERENCE: "Välj Ljust, Mörkt eller System.",
  PORTAL_INVALID_REQUEST_TYPE: "Det finns ingen sådan typ av ärende.",
  PORTAL_MEDIA_TYPE_NOT_ALLOWED: "Den filtypen går inte att ladda upp.",
  PORTAL_INVALID_ATTACHMENT_BUCKET: "Filen kunde inte tas emot. Försök igen.",
  PORTAL_INVALID_CONTENT_SIGNATURE_STATUS: "Filen kunde inte tas emot. Försök igen.",
  PORTAL_INVALID_LEGAL_FORM: "Det är inte en giltig bolagsform.",
  PORTAL_INVALID_MEMBERSHIP_ROLE: "Det är inte en giltig roll.",
  PORTAL_INVALID_SERVICE_TYPE: "Det är inte en giltig tjänstetyp.",
  PORTAL_INVALID_SERVICE_LIFECYCLE_STATUS: "Det är inte ett giltigt läge för tjänsten.",
  PORTAL_INVALID_END_REASON: "Det är inte ett giltigt skäl för att avsluta förvaltningen.",
  // Build round 12, 2026-10-07: the client's cancellation and its withdrawal, recorded by Petrovic
  // on the Avslut tab (`20261007100001`). Operator-facing, so they name the act rather than "ni".
  PORTAL_NOT_A_CLIENT_CANCELLATION:
    "Uppsägningen kommer från Petrovic Systems och inte från kunden, så den ångras inte här. Ingenting ändrades.",
  PORTAL_CANCELLATION_END_DATE_PASSED:
    "Slutdatumet har passerat, så uppsägningen går inte att ångra längre. Ingenting ändrades.",
  PORTAL_CANCELLATION_REASON_TOO_LONG:
    "Anledningen är längre än 2000 tecken. Korta den, så sparas den som kunden skrev den.",
  // Build round 14, 2026-10-07: Petrovic takes back its own termination (`20261007300001`).
  PORTAL_NOT_A_PETROVIC_TERMINATION:
    "Uppsägningen kommer från kunden, så den tas tillbaka under Ångra kundens uppsägning. Ingenting ändrades.",
  PORTAL_TERMINATION_END_DATE_PASSED:
    "Slutdatumet har passerat, så uppsägningen går inte att ta tillbaka längre. Ingenting ändrades.",
  // Build round 13, 2026-10-07: Petrovic's own termination follows the terms (`20261007200001`).
  PORTAL_NOTICE_PERIOD_NOT_MET:
    "Slutdatumet ger kortare än en månads varsel från utgången av den fakturerade månaden. Välj ett senare datum. Ingenting ändrades.",
  PORTAL_END_DATE_IN_THE_PAST: "Slutdatumet ligger före i dag. Ingenting ändrades.",
  PORTAL_END_DATE_REQUIRED: "Skälet har inget slutdatum i villkoren, så det måste skrivas in. Ingenting ändrades.",
  PORTAL_INVALID_OWNERSHIP_STATUS: "Det är inte ett giltigt ägarläge.",
  PORTAL_INVALID_MFA_STATUS: "Det är inte ett giltigt läge för tvåstegsverifiering.",
  PORTAL_INVALID_PERSON_KIND: "Det är inte en giltig typ av person.",
  PORTAL_INVALID_ACCESS_EVENT_TYPE: "Det är inte en giltig åtkomsthändelse.",
  PORTAL_INVALID_SCOPE_RESULT: "Det är inte en giltig klassning.",
  PORTAL_INVALID_REQUEST_STATUS: "Det är inte ett giltigt läge för ärendet.",
  /*
   * Build round A1, 2026-10-08 (`20261008500001`): the management tier, Petrovic's own requests and
   * the final "Avböjt". Every one is raised at Petrovic, so they name the act rather than "ni". The
   * forms refuse each of them before sending where they can; these are the answer when the screen
   * had gone stale.
   *
   * `PORTAL_CROSS_TENANT_REQUEST` and `PORTAL_EMPTY_REQUEST` are older and had no sentence. They are
   * added now because `admin_create_request` and `admin_decline_request` are the first admin forms
   * that reach them in ordinary use. `PORTAL_NO_TENANT_COUNTER` still has none, for the reason
   * `useAdminConvertMessageToRequest` gives: a tenant reachable from the register has a counter.
   */
  PORTAL_INVALID_MANAGEMENT_TIER: "Det är inte en giltig nivå. Välj Förvaltning eller Utveckling.",
  PORTAL_MANAGEMENT_TIER_BEFORE_START:
    "Datumet ligger före förvaltningens startdatum. Välj ett datum från starten och framåt. Ingenting ändrades.",
  PORTAL_MANAGEMENT_TIER_SAME_DAY:
    "Nivån har redan ändrats med det datumet. Välj ett annat datum. Ingenting ändrades.",
  PORTAL_MANAGEMENT_TIER_UNCHANGED:
    "Kunden har redan den nivån det datumet. Ingenting ändrades.",
  /*
   * Build round 25, 2026-10-09 (`20261009100001`): the agreed monthly price, raised at Petrovic by
   * `admin_activate_management` and `admin_set_management_price`. The forms refuse each of them
   * before sending; these are the answer when the screen had gone stale. A missing price on a live
   * period is `PORTAL_INVALID_MANAGEMENT_TRANSITION`'s, which already has its sentence above.
   */
  PORTAL_MANAGEMENT_PRICE_REQUIRED:
    "Förvaltningen startar inte utan ett pris. Skriv priset per månad från kundens uppdragsbekräftelse. Ingen förvaltning startades.",
  PORTAL_INVALID_MANAGEMENT_PRICE:
    "Priset måste vara ett helt antal kronor större än noll. Ingenting ändrades.",
  PORTAL_MANAGEMENT_PRICE_BEFORE_START:
    "Datumet ligger före förvaltningens startdatum. Välj ett datum från starten och framåt. Ingenting ändrades.",
  PORTAL_MANAGEMENT_PRICE_SAME_DAY:
    "Priset har redan ändrats med det datumet. Välj ett annat datum. Ingenting ändrades.",
  PORTAL_MANAGEMENT_PRICE_UNCHANGED:
    "Kunden har redan det priset det datumet. Ingenting ändrades.",
  PORTAL_DECLINE_REASON_REQUIRED: "Skriv varför ärendet avböjs. Kunden läser skälet. Ingenting ändrades.",
  PORTAL_DECLINE_REASON_TOO_LONG: "Skälet får vara högst 500 tecken. Korta det. Ingenting ändrades.",
  PORTAL_REQUEST_IS_FINAL:
    "Ärendet är redan klart eller avböjt och kan inte ändras. Ladda om sidan så visar den vad som gäller nu.",
  PORTAL_CROSS_TENANT_REQUEST: "Ärendet hör till ett annat kundkonto eller finns inte längre. Ingenting ändrades.",
  /*
   * Build round 54, 2026-10-31 (`20261101100001`): the two refusals of `admin_update_request_status`
   * that had no sentence. Klart was never offered by the status control, so the first is the answer
   * for a screen that has gone stale. The second is also refused by Handläggning before it sends,
   * with this same sentence (`WAITING_NOTE_REQUIRED` in `app/routes/AdminRequestsRoute.tsx`).
   */
  PORTAL_USE_ADMIN_CLOSE_REQUEST:
    "Klart sätts när du stänger ärendet under Stäng ärendet och anger minuterna. Ingenting ändrades.",
  PORTAL_WAITING_NOTE_REQUIRED:
    "Skriv vad du väntar på innan du väljer Väntar på kunden. Kunden läser texten. Ingenting ändrades.",
  // One sentence for two gateways with two rules: the client's `create_request` wants a title, a
  // description or a file, and `admin_create_request` wants both a title and a description. What is
  // true of both is that text was missing and nothing was created.
  PORTAL_EMPTY_REQUEST: "Rubrik eller beskrivning saknas, så ärendet skapades inte.",
  /*
   * Build round A2, 2026-10-08 (`20261008600001`): the monthly cap with a soft stop and "Dela upp
   * ärendet". Every one is raised at Petrovic. The caps are named without their numbers, for the
   * reason the site-publish caps below are: the numbers live in `private.request_monthly_caps()` and
   * the screen shows them beside the counts. The request page offers only the choices the counts on
   * screen allow, so these are the answers for a screen that has gone stale.
   */
  PORTAL_MONTHLY_CAP_REACHED:
    "Månadens små ändringar är redan gjorda, så ärendet kan inte räknas som Ingår i förvaltning. Välj Extra, Nästa månad eller Eget pris under Över månadens tak. Ingenting ändrades.",
  PORTAL_INVALID_OVER_CAP_CHOICE: "Välj Extra, Nästa månad eller Eget pris. Ingenting ändrades.",
  PORTAL_REQUEST_NOT_OVER_CAP:
    "Ärendet är inte över månadens tak längre, eller räknas aldrig mot det. Ladda om sidan så visar den vad som gäller nu. Ingenting ändrades.",
  PORTAL_NEXT_MONTH_FULL:
    "Nästa månads små ändringar är redan gjorda, så ärendet kan inte flyttas dit. Välj Extra eller Eget pris. Ingenting ändrades.",
  PORTAL_NEXT_MONTH_HAS_NO_MANAGEMENT:
    "Kunden har ingen förvaltning nästa månad, så ärendet kan inte räknas där. Välj Extra eller Eget pris. Ingenting ändrades.",
  PORTAL_INVALID_SPLIT:
    "En del saknar rubrik, en text är för lång eller så är delarna fler än nio. Inget nytt ärende skapades och ingenting ändrades.",
  PORTAL_INVALID_MONITOR_KIND: "Det är inte en giltig typ av övervakning.",
  PORTAL_INVALID_CAUSE_ATTRIBUTION: "Det är inte en giltig orsak.",
  PORTAL_INVALID_PRODUCT_TYPE: "Det är inte en giltig produkttyp.",
  PORTAL_INVALID_DISPLAY_CATEGORY: "Det är inte en giltig dokumentkategori.",
  PORTAL_INVALID_INSIGHT_SOURCE_TYPE: "Det är inte en giltig källa.",
  PORTAL_UNKNOWN_INSIGHT_TYPE: "Det är inte en giltig typ av insikt.",
  PORTAL_INVALID_INSIGHT_SEVERITY: "Det är inte en giltig allvarlighetsgrad.",
  PORTAL_INVALID_OPERATING_NOTE_TYPE: "Det är inte en giltig typ av anteckning.",
  PORTAL_INVALID_OFFBOARDING_TASK_STATUS: "Det är inte ett giltigt läge för uppgiften.",
  PORTAL_INVALID_ALERT_SOURCE: "Det är inte en giltig källa för larmet.",
  PORTAL_INVALID_ALERT_SEVERITY: "Det är inte en giltig nivå för larmet.",
  // Radera kund, migration 20260915000006. The evidence refusal names what stops the erasure in the
  // panel's own list, so this sentence only has to say that something does.
  PORTAL_ERASURE_NAME_MISMATCH: "Namnet stämmer inte. Skriv kundens namn exakt som det står.",
  PORTAL_ERASURE_BLOCKED_BY_CONTRACT_EVIDENCE:
    "Kunden har avtalsbevis från ett ingånget avtal och kan inte raderas. Bevisen står i listan.",
  PORTAL_ERASURE_BLOCKED_BY_RETENTION_HOLD:
    "Det finns en aktiv spärr för gallring som gäller kunden. Släpp spärren först om raderingen ska göras.",
  PORTAL_ERASURE_STEP_UP_TOTP_REQUIRED: "Radering kräver en kod från din autentiseringsapp.",
  // Karantän för en bilaga, migration 20260915000007.
  PORTAL_INVALID_QUARANTINE_REASON:
    "Skriv ett skäl på mellan tio och femhundra tecken, på en rad.",
  PORTAL_UNKNOWN_ATTACHMENT: "Filen finns inte hos den här kunden.",
  PORTAL_ATTACHMENT_NOT_QUARANTINABLE:
    "Filen är redan satt åt sidan eller borttagen. Ingenting ändrades.",
  PORTAL_QUARANTINE_STEP_UP_TOTP_REQUIRED: "Karantän kräver en kod från din autentiseringsapp.",
  // Ändra e-postadress, migration 20260922200001, `public.admin_change_contact_email`. An operator
  // reads every one of these about a contact he has just picked, so each says what to do next and
  // none addresses the reader as "ni". The form refuses the empty, the unchanged and the malformed
  // address before sending, so the first three arrive only when the list on screen was stale.
  PORTAL_INVALID_EMAIL: "Adressen ser inte ut som en e-postadress. Kontrollera stavningen.",
  PORTAL_UNKNOWN_CONTACT: "Kontakten finns inte hos den här kunden. Ladda om sidan.",
  PORTAL_CONTACT_INACTIVE:
    "Kontakten är inaktiv, och en inaktiv kontakts adress ändras inte. Ladda om sidan.",
  PORTAL_EMAIL_UNCHANGED: "Kontakten har redan den adressen. Ingenting ändrades.",
  PORTAL_EMAIL_IN_USE:
    "Adressen används redan av en annan aktiv kontakt. Två personer kan inte dela inloggningsadress.",
  PORTAL_CONTACT_INVITATION_OPEN:
    "Kontakten har en inbjudan som inte är använd. Dra tillbaka den först och bjud in igen till den nya adressen.",
  PORTAL_EMAIL_CHANGE_PENDING:
    "Ett byte av kontaktens adress väntar redan. Avbryt det under Väntar på bekräftelse om det ska ersättas.",
  PORTAL_CONTACT_HAS_SEVERAL_ACCOUNTS:
    "Kontakten är kopplad till mer än ett konto i portalen, så portalen vet inte vilken inloggning som ska ändras. Ingenting ändrades.",
  PORTAL_ACCOUNT_SHARED_ACROSS_CLIENTS:
    "Kontot används också hos en annan kund. En ändring här skulle ändra inloggningen där också, så den görs inte härifrån.",
  // Also raised by the admin scripts' recovery path, which prints the code and never this sentence.
  PORTAL_ADMIN_ACCOUNT_NOT_RECOVERABLE:
    "Kontot är ett Petrovic Systems-konto. Dess adress ändras inte från en kunds sida.",
  PORTAL_EMAIL_CHANGE_STEP_UP_TOTP_REQUIRED:
    "Att ändra en adress kräver en kod från din verifieringsapp.",
  // Bekräfta den nya adressen, round C, migration 20260922230001. The first four are met by the
  // person holding the link; `ConfirmEmailChangeRoute` has its own sentences for them, written to
  // that person, and these are for any other reader. The last two are Petrovic's, on Avbryt.
  PORTAL_EMAIL_CHANGE_LINK_INVALID:
    "Länken gick inte att använda. Den fungerar bara för kontot som bytet gäller. Ingenting har ändrats.",
  PORTAL_EMAIL_CHANGE_LINK_USED: "Länken är redan använd. Bytet är bekräftat.",
  PORTAL_EMAIL_CHANGE_LINK_EXPIRED: "Länken har gått ut. Ingenting har ändrats.",
  PORTAL_EMAIL_CHANGE_FACTOR_REQUIRED:
    "Logga in med koden från verifieringsappen först. Ingenting har ändrats.",
  PORTAL_NO_EMAIL_CHANGE_PENDING: "Kontakten har inget väntande byte. Ladda om sidan.",
  PORTAL_EMAIL_CHANGE_ALREADY_CONFIRMED:
    "Bytet är redan bekräftat och genomförs nu, så det går inte att avbryta.",
  // Ändra tjänst, the same migration, `public.admin_update_service`. `PORTAL_CROSS_TENANT_SERVICE`
  // above already covers another client's service and an unknown one.
  PORTAL_INVALID_SERVICE_URL:
    "Adressen måste börja med http:// eller https:// och får vara högst 2048 tecken.",
  PORTAL_INVALID_PUBLICATION_DATE:
    "Publiceringsdatumet kan inte ligga i framtiden eller före år 2000.",
  PORTAL_SERVICE_UNCHANGED: "Tjänsten har redan de uppgifterna. Ingenting ändrades.",
  PORTAL_PUBLICATION_DATE_LOCKED:
    "Förvaltningen har redan startat från den här tjänsten, och startdatumet är läst från publiceringsdatumet. Datumet går inte att ändra i efterhand.",
  // Arkivera and Återställ, build round 46 (2026-10-17), `public.admin_archive_service` and
  // `public.admin_restore_service`. PORTAL_SERVICE_ARCHIVED is what every other write on an archived
  // service answers, the client's new request among them.
  PORTAL_SERVICE_ARCHIVED:
    "Tjänsten är avslutad, så det går inte att lägga till något nytt på den. Ingenting sparades.",
  PORTAL_SERVICE_ALREADY_ARCHIVED: "Tjänsten är redan arkiverad. Ingenting ändrades.",
  PORTAL_SERVICE_NOT_ARCHIVED: "Tjänsten är inte arkiverad. Ingenting ändrades.",
  PORTAL_SERVICE_IS_MANAGEMENT_BASIS:
    "Förvaltningen startade från den här tjänsten och pågår fortfarande. Avsluta förvaltningen först, sedan går tjänsten att arkivera. Ingenting ändrades.",
  PORTAL_UNKNOWN_TOPIC: "Kanalen finns inte.",
  PORTAL_NO_CHAT_THREAD: "Det finns ingen konversation att läsa ännu.",
  // Raised by `mark_notification_read` when the row is not the caller's, which is the same answer it
  // gives when the row does not exist at all. That is deliberate on the server's side and the
  // sentence keeps it: it says the notification is gone, not that it belongs to somebody else.
  PORTAL_UNKNOWN_NOTIFICATION: "Notisen finns inte längre.",
  // Raised by `admin_acknowledge_operational_alert` when the alert is neither open nor acknowledged,
  // which is the same answer it gives for an alert that does not exist. The likely cause on
  // `/admin/operations` is a race rather than a mistake: several of these alerts resolve themselves when
  // the condition they measure clears, so the row on screen can be stale by the time it is clicked.
  // The sentence says nothing changed, because nothing did.
  PORTAL_UNKNOWN_OPERATIONAL_ALERT:
    "Larmet är inte öppet eller kvitterat längre. Ingenting ändrades.",
  // Raised by `admin_offboarding_tasks` for a run id it cannot find. The gateway checks the id
  // rather than returning an empty list, so that an unknown run says so by name instead of looking
  // like a checklist with no points on it, and the sentence keeps that apart.
  PORTAL_UNKNOWN_OFFBOARDING_RUN: "Avslutet finns inte längre. Ladda om sidan.",
  // Raised by `admin_complete_offboarding_task` when the task is not pending, which is the same
  // answer it gives for a task that does not exist. On `/admin/operations` the likely cause is a stale
  // row: the last completion closes the run, so a checklist left open in another tab can be
  // answering about work that is already done.
  PORTAL_UNKNOWN_OFFBOARDING_TASK: "Punkten är inte öppen längre. Ingenting ändrades.",
  // Raised by `admin_complete_offboarding_task` only for `deliver_processor_attachments`, and only
  // for `not_applicable`. The eight tasks the generator seeds are untouched. For the delivery, ej
  // tillämplig ends in the same place as never doing it: the files go at the twelve-month deadline
  // and the record says the obligation was handled. That is the answer that needs a reason beside it.
  PORTAL_DELIVERY_NOT_APPLICABLE_NEEDS_NOTE:
    "Skriv ett underlag först. Utlämnandet kan bara stängas som ej tillämpligt med en mening om varför, eftersom filerna raderas vid tolvmånadersgränsen oavsett vad raden säger.",
  // Raised by `admin_resolve_membership_request` for a request that is already resolved and for one
  // that does not exist. Both mean the board on screen is older than the table.
  PORTAL_UNKNOWN_MEMBERSHIP_REQUEST:
    "Förfrågan är redan besvarad eller finns inte. Ingenting ändrades.",
  // Raised by `admin_release_retention_hold` for a hold that is already released and for one that
  // does not exist, because it updates `where released_at is null`. On `/admin/operations` the likely
  // cause is a second tab: the release control is offered only for an unreleased hold, so a list
  // that is older than the table can still offer it.
  PORTAL_UNKNOWN_RETENTION_HOLD: "Stoppet är redan släppt. Ingenting ändrades.",
  // The five refusals `admin_create_retention_hold` gained in migration 67. Every one of them was a
  // check constraint before, arriving as `23514` with no portal code and rendering as the fallback,
  // so an operator was told "something went wrong" about a field in front of them. The one that was
  // more than a message is the tenant scope: the constraint permitted a tenant hold with no tenant,
  // `private.retention_is_held` can never match such a row, and it rendered as an active hold that
  // suspended nothing.
  PORTAL_UNKNOWN_HOLD_SCOPE: "Välj vad stoppet ska gälla.",
  PORTAL_HOLD_TENANT_REQUIRED: "Ett stopp för en kund måste peka ut kunden.",
  PORTAL_HOLD_CLASS_REQUIRED: "Ett stopp för en gallringsklass måste peka ut klassen.",
  PORTAL_HOLD_RECORD_REQUIRED: "Ett stopp för en enskild rad måste peka ut raden.",
  PORTAL_HOLD_REASON_REQUIRED: "Skriv en anledning på minst tio tecken.",

  /* --- `/admin/records`, plan.md sections 6.3, 6.4 and 6.5 ------------------------------------ */

  // Raised by `admin_handle_invoice_reminder` when the reminder is already handled or has been
  // cancelled, which is the same answer it gives for one that does not exist. On `/admin/records`
  // the likely cause is a stale row rather than a mistake: the scheduled job cancels an unhandled
  // reminder once a cancellation puts it past the confirmed end date, so a list left open in
  // another tab can be answering about a month nobody owes.
  PORTAL_UNKNOWN_INVOICE_REMINDER: "Påminnelsen är inte öppen längre. Ingenting ändrades.",
  // Raised by `admin_set_invoice_schedule` for a day outside 1 to 28, which is what
  // `invoice_schedules_billing_day_check` allows. The screen refuses the same range before it calls
  // anything, so this arrives only if the two ever disagree, and a rule stated in two places is a
  // rule that can be changed in one of them. The sentence gives the reason rather than the range
  // alone: a monthly reminder on the 29th is a reminder that does not happen in February.
  PORTAL_INVOICE_BILLING_DAY_OUT_OF_RANGE:
    "Fakturadagen måste vara mellan 1 och 28. Februari har ingen 29:e varje år, så en månatlig påminnelse senare i månaden skulle hoppa över den.",
  // Raised by `admin_resolve_customer_insight` for an insight that is already resolved, which is the
  // same answer it gives for one that does not exist. Resolving is a one-way act by design: the
  // migration records that an honest correction is a second insight and never an edit that erases
  // the first, so a second answer on the same row is refused rather than applied.
  PORTAL_UNKNOWN_CUSTOMER_INSIGHT: "Insikten är redan besvarad. Ingenting ändrades.",

  /* --- `/admin/clients`, plan.md sections 5.2, 6.1 and 6.2 ------------------------------------- */

  // Raised by `admin_client_chat` on errcode 42501 and by `admin_create_contact` on 23503, both for
  // a tenant id that is not in `public.tenants` at all. On `/admin/clients` the id was taken from a
  // register row or an inbox item a moment earlier, so the honest reading is that the screen is
  // older than the table rather than that somebody typed something wrong.
  PORTAL_UNKNOWN_TENANT: "Kundkontot finns inte längre. Ladda om sidan.",
  // Raised by `admin_mark_client_chat_read`, `mark_chat_read`, `convert_message_to_request` and
  // `admin_convert_message_to_request`, in every case for a message id that is not in the tenant the
  // call names. That is one message id and one tenant id disagreeing, and it covers a message that
  // does not exist at all, so the sentence says the message rather than accusing the reader of
  // reaching for another client's data.
  PORTAL_CROSS_TENANT_MESSAGE:
    "Meddelandet hör inte till den här kunden. Ingenting ändrades.",

  /* --- Bilagor, plan.md sections 5.2 and 5.3 --------------------------------------------------- */

  // The four refusals on the *upload* half, added 2026-09-12 when the client got a way to reach it.
  //
  // Every code below has existed in `public.begin_attachment_upload` and
  // `public.finalize_attachment_upload` since Batch B, and none of them had ever been raised at a
  // person: `/api/files/upload-intent` had no caller in `app/` until this round, so the only client
  // attachments in the system arrived through a fixture. Until they were named here each one
  // rendered as "Något gick fel. Försök igen.", which is the same sentence a dropped connection
  // gets, and the difference between the two is the whole of what the person can do next.
  //
  // The browser's own pre-flight refusals are deliberately not here. A wrong file type, a file over
  // the ceiling and an empty selection are all decided in `app/data/attachmentQueries.ts` before
  // anything is sent, and they name the file in front of the person. This file's opening rule is
  // that the submitted value is never part of the message, so those sentences cannot live here.
  PORTAL_ATTACHMENT_LIMIT_REACHED:
    "Ett meddelande kan bära högst fem filer. Ta bort en fil innan ni lägger till en ny.",
  // Raised by `finalize_attachment_upload` for an attachment that is already `available` or
  // `quarantined_invalid`. The repair for a failed upload is a new one, never a second verdict on
  // the same row, which is what the 2026-09-08 single-use marker on `availability_status` decided.
  PORTAL_ATTACHMENT_ALREADY_FINALIZED:
    "Filen är redan färdigbehandlad. Bifoga den på nytt om ni vill skicka den igen.",
  // Raised by `finalize_attachment_upload` when the caller is not `uploaded_by_user_id`. Reachable
  // by a second member of the same tenant, which is why the sentence says who rather than what.
  PORTAL_ATTACHMENT_NOT_UPLOADER:
    "Bara den som började uppladdningen kan slutföra den. Bifoga filen på nytt.",
  // Raised by `finalize_attachment_upload` for an attachment that is not in the tenant named. Same
  // shape as `PORTAL_CROSS_TENANT_MESSAGE` above and for the same reason: it covers an attachment
  // that does not exist at all, so it says the file rather than accusing the reader.
  PORTAL_CROSS_TENANT_ATTACHMENT: "Filen hör inte till det här kundkontot. Ingenting skickades.",
  // `public.add_request_comment`'s half of `PORTAL_EMPTY_MESSAGE`, which has been at the top of this
  // file since the first slice while its twin was missing. Neither gateway reads
  // `public.attachments`, so an attachment-only comment is refused by the server today and both
  // composers require text. Measured 2026-09-12; `schema.md`'s note that the write gateway enforces
  // "body or at least one finalized attachment" across tables describes a rule no gateway has.
  PORTAL_EMPTY_COMMENT: "Skriv en kommentar innan du skickar.",

  /* --- Konto, inloggningar och behörighet, plan.md sections 4 and 12 --------------------------- */

  // Raised by `public.request_membership_change` when the same contact already has an unresolved
  // request inside its fourteen days. The guard sits in front of the rate limit and in front of the
  // step-up consume, so nothing was spent and the sentence can honestly invite a look rather than a
  // retry. The likely cause is a person who pressed once, saw no confirmation, and pressed again.
  PORTAL_MEMBERSHIP_REQUEST_ALREADY_OPEN:
    "Det finns redan en öppen förfrågan för den personen. Den ligger kvar i listan nedanför tills vi svarat.",
  // The second entry in this table the client decides for itself, after `PORTAL_OFFLINE`, and it
  // covers the one failure that must not read as "nothing happened". `Logga ut andra enheter` is two
  // calls: Supabase Auth ends the sessions with its own `others` scope, and the gateway then marks
  // the rows. The order is the safety property. When the second one fails the sessions are genuinely
  // gone, so a generic failure sentence would tell somebody acting on a suspicion that their action
  // did not work when it did.
  PORTAL_SESSIONS_ENDED_LIST_STALE:
    "De andra inloggningarna är avslutade. Listan hann inte uppdateras. Ladda om sidan så visar den vad som gäller nu.",

  // The four refusals the attachment download path can produce. They lived as a local map in
  // `app/data/adminClientQueries.ts` until 2026-09-04 and are here now, because two places for one
  // code is the exact thing this file's opening paragraph exists to prevent.
  //
  // `PORTAL_ATTACHMENT_NOT_AVAILABLE` is raised by `issue_attachment_download_ticket` and by
  // `admin_issue_attachment_download_ticket` for an attachment that is absent, pending, quarantined,
  // returned or deleted, and for a storage object that is gone or past its retention date. One
  // refusal for all seven is the migration's own decision and it is not vagueness: the difference
  // between them is what a caller working through guessed ids is trying to learn. The screen that
  // offers the control already knows the status from its own list, so the sentence points back at
  // the list rather than guessing which of the seven it was.
  PORTAL_ATTACHMENT_NOT_AVAILABLE:
    "Filen går inte att öppna. Ladda om sidan, så säger listan vilket läge den står i nu.",
  // The three from `public.consume_attachment_download_ticket`, and they are reachable rather than
  // theoretical: a ticket lives 45 seconds and is single use, so a slow network between the mint and
  // the fetch expires it and a double click spends it twice. All three are answered by pressing
  // Öppna again, and only the third one has a number worth saying out loud.
  PORTAL_TICKET_NOT_FOUND: "Nedladdningen gick inte att lösa in. Tryck Öppna igen.",
  PORTAL_TICKET_CONSUMED: "Nedladdningen är redan förbrukad. Tryck Öppna igen.",
  PORTAL_TICKET_EXPIRED: "Nedladdningen hann gå ut. Den gäller i 45 sekunder. Tryck Öppna igen.",
  /*
   * The change-order gateways, added 2026-09-24 when polish round 1 gave them a screen on
   * `/admin/clients` (P-4, part C2). Admin only. `app/routes/AdminChangeOrderSection.tsx` refuses an
   * empty reference, a reference already on screen, a missing offer and an answer time before sending
   * or in the future before anything leaves, so these are the answers for a screen that has gone
   * stale and for the cases the browser cannot see.
   */
  PORTAL_CHANGE_ORDER_REFERENCE_REQUIRED: "Skriv en referens för ändringsbeställningen. Ingenting skickades.",
  PORTAL_CHANGE_ORDER_REFERENCE_TAKEN:
    "Referensen används redan av en annan ändringsbeställning hos kunden. Välj en annan.",
  PORTAL_CHANGE_ORDER_ASSIGNMENT_NOT_FOUND:
    "Uppdraget finns inte hos kunden längre. Ladda om sidan.",
  PORTAL_CHANGE_ORDER_SERVICE_NOT_FOUND: "Tjänsten finns inte hos kunden längre. Ladda om sidan.",
  PORTAL_CHANGE_ORDER_NOT_FOUND: "Ändringsbeställningen finns inte hos kunden. Ladda om sidan.",
  PORTAL_CHANGE_ORDER_TRANSITION_INVALID:
    "Ändringsbeställningen står inte i det läge åtgärden gäller. Ladda om sidan så visar den vad som gäller nu.",
  PORTAL_CHANGE_ORDER_VERSION_NOT_FOUND:
    "Offertdokumentet finns inte hos kunden. Ingenting ändrades.",
  PORTAL_CHANGE_ORDER_VERSION_NOT_AVAILABLE:
    "Offertdokumentets fil är inte godkänd eller är borttagen, så den kan inte skickas. Ingenting ändrades.",
  PORTAL_CHANGE_ORDER_APPROVED_AT_INVALID:
    "Tidpunkten måste ligga efter att offerten skickades och får inte ligga i framtiden. Ingenting ändrades.",
  PORTAL_CHANGE_ORDER_SENT_AT_INVALID:
    "Fyll i när offerten mejlades. Tidpunkten får inte ligga i framtiden. Ingenting ändrades.",
  /*
   * The assignment gateways, added 2026-09-24 when polish round 2 gave them the Nytt uppdrag section
   * on `/admin/clients`. Admin only. `app/routes/AdminAssignmentSection.tsx` refuses an empty
   * reference, a reference already on screen and dates that are missing, in the future or in the
   * wrong order before anything leaves, so these are the answers for a stale screen and for the
   * database clock disagreeing with the browser's.
   */
  PORTAL_ASSIGNMENT_REFERENCE_REQUIRED: "Skriv en referens för uppdraget. Ingenting skickades.",
  PORTAL_ASSIGNMENT_REFERENCE_TAKEN:
    "Referensen används redan av ett annat uppdrag hos kunden. Välj en annan.",
  PORTAL_ASSIGNMENT_SERVICE_NOT_FOUND: "Tjänsten finns inte hos kunden längre. Ladda om sidan.",
  PORTAL_ASSIGNMENT_NOT_FOUND: "Uppdraget finns inte hos kunden. Ladda om sidan.",
  PORTAL_ASSIGNMENT_TRANSITION_INVALID:
    "Uppdraget står inte i det läge åtgärden gäller. Ladda om sidan så visar den vad som gäller nu.",
  PORTAL_ASSIGNMENT_SENT_AT_INVALID:
    "Fyll i när uppdragsbekräftelsen mejlades. Tidpunkten får inte ligga i framtiden. Ingenting ändrades.",
  PORTAL_ASSIGNMENT_CONFIRMED_AT_INVALID:
    "Tidpunkten för kundens ja måste ligga efter att bekräftelsen skickades och får inte ligga i framtiden. Ingenting ändrades.",
  /*
   * The client's keys, build round 5 (2026-09-27), the Nycklar box on Tjänster och förvaltning.
   * `app/routes/adminClient/KeysBox.tsx` checks the lengths before sending, so the text refusal is the
   * answer for what the browser cannot judge: a control character or a line break where none belongs.
   * The value refusal is the gateway's guess that a field holds a pasted token, and the sentence says
   * so without echoing the field back.
   */
  PORTAL_INVALID_CREDENTIAL_TEXT:
    "Ett fält är tomt, för långt eller har tecken som inte hör hemma där. Bara stegen för bytet får ha radbrytningar. Ingenting sparades.",
  PORTAL_CREDENTIAL_VALUE_REFUSED:
    "Ett fält ser ut att innehålla själva nyckeln. Värdet sparas aldrig här. Skriv var nyckeln ligger i stället. Ingenting sparades.",
  PORTAL_INVALID_CREDENTIAL_EXPIRY: "Utgångsdatumet kan inte ligga före år 2000. Ingenting sparades.",
  PORTAL_UNKNOWN_CREDENTIAL:
    "Nyckeln finns inte hos kunden eller är redan återkallad. Ladda om sidan.",
  PORTAL_UNKNOWN_ACCOUNT_REFERENCE: "Kontot finns inte hos kunden längre. Ladda om sidan.",
  /*
   * Publishing a client's website, build round 17 (2026-10-07), on Tjänster och förvaltning. The two
   * caps are named without their numbers: the numbers live in `private.site_publish_caps()` and the
   * screen shows them beside the counts, so a changed cap cannot leave a sentence here saying the
   * old one.
   */
  PORTAL_NOT_A_WEBSITE_SERVICE: "Bara en webbplats kan publiceras, och den här tjänsten är något annat. Ingenting ändrades.",
  PORTAL_INVALID_SITE_REPOSITORY:
    "Skriv repot som ägare/namn, till exempel PetrovicSystems/sanells-trafikskola. Ingenting sparades.",
  PORTAL_SITE_REPOSITORY_OWNER_REFUSED: "Repot måste ligga hos PetrovicSystems (tidigare Petrovic-AB) på GitHub. Ingenting sparades.",
  PORTAL_SITE_REPOSITORY_IS_THE_PORTAL:
    "Det är kundportalens eget repo, och det kan aldrig vara en kunds webbplats. Ingenting sparades.",
  PORTAL_SITE_REPOSITORY_NOT_SET: "Tjänsten har inget GitHub-repo. Ange repot först, sedan går den att publicera.",
  PORTAL_SITE_PUBLISH_ALREADY_QUEUED:
    "En publicering väntar redan på att skickas till GitHub. Den går i väg inom en minut.",
  PORTAL_SITE_PUBLISH_MONTHLY_CAP:
    "Månadens tak för publiceringar är nått, för alla kunder tillsammans. Nästa går att starta den första i nästa månad.",
  PORTAL_SITE_PUBLISH_CLIENT_DAILY_CAP:
    "Kundens tak för publiceringar i dag är nått. Nästa går att starta i morgon.",
  PORTAL_UNKNOWN_SITE_PUBLISH: "Publiceringen finns inte. Ladda om sidan.",
  /*
   * A website's facts, build round 18 (2026-10-07), the Fakta sheet on Tjänster och förvaltning. The
   * two "changed" refusals are a form that was built from an older read, so both say to load the page
   * again; the invalid one names no field, because the sheet marks each field the database refused
   * from the problems the refusal carries.
   */
  PORTAL_SITE_FACTS_SCHEMA_READ_QUEUED: "En läsning av schemat väntar redan. Den går i väg inom en minut.",
  PORTAL_SITE_FACTS_NO_SCHEMA: "Inget schema är läst från repot än. Läs schemat först. Ingenting sparades.",
  PORTAL_SITE_FACTS_SCHEMA_CHANGED:
    "Schemat har lästs om sedan formuläret eller utkastet byggdes. Ladda om sidan och spara utkastet igen. Ingenting ändrades.",
  PORTAL_SITE_FACTS_DRAFT_CHANGED:
    "Utkastet har sparats någon annanstans sedan formuläret öppnades. Ladda om sidan så visar den det som är sparat nu. Ingenting ändrades.",
  PORTAL_SITE_FACTS_INVALID: "Värdena följer inte schemat. Ingenting ändrades.",
  PORTAL_SITE_FACTS_TOO_LARGE: "Faktan är för stor för att sparas. Ingenting sparades.",
  PORTAL_SITE_FACTS_NO_DRAFT: "Det finns inget sparat utkast att publicera. Spara utkastet först.",
  PORTAL_SITE_FACTS_UNCHANGED:
    "Utkastet är likadant som den senast publicerade versionen, så ingen ny version skapades.",
  /*
   * The client's own form, build round 19 (2026-10-08). The client's screen says these in its own
   * words (`app/routes/clientFacts/`); these are the sentences for anywhere else the codes surface.
   */
  PORTAL_SITE_FACTS_LOCKED:
    "Några av uppgifterna kan bara Petrovic Systems ändra. Skriv till oss i chatten om något av dem ska ändras. Ingenting sparades.",
  PORTAL_SITE_FACTS_NOT_PUBLISHED:
    "Webbplatsen har inga publicerade uppgifter än. Petrovic Systems publicerar den första versionen.",
  PORTAL_UNKNOWN_SITE_FACTS_NOTICE: "Notisen finns inte längre. Ladda om sidan.",
  /*
   * Petrovic's own notice choices, build round 23 (2026-10-08),
   * `public.admin_set_notification_preference`. The screen never offers it (the critical alarm's row
   * is disabled), so it is the answer for a screen the database has moved past. The same gateway's
   * unknown category is `PORTAL_INVALID_NOTIFICATION_CATEGORY` above, which the client's preference
   * gateway has always raised and whose sentence is true from either side.
   */
  PORTAL_NOTIFICATION_MANDATORY: "Kritiska driftlarm går inte att stänga av. Ingenting ändrades.",
  // Build round 40, 2026-10-11: the onboarding gates. No invitation, analytics connection or Search
  // Console property without an approved, published personuppgiftsbiträdesavtal of the right edition.
  PORTAL_PROCESSOR_AGREEMENT_REQUIRED:
    "Kunden har inget godkänt personuppgiftsbiträdesavtal. Publicera avtalet på kundens hylla och registrera godkännandet som nämner det, sedan går det. Ingenting ändrades.",
  PORTAL_PROCESSOR_AGREEMENT_TOO_OLD:
    "Det godkända personuppgiftsbiträdesavtalet är en äldre avtalstext som inte omfattar den här statistiken. Kunden behöver godkänna den nya texten först. Ingenting ändrades.",
  PORTAL_PROCESSOR_AGREEMENT_EDITION_REQUIRED:
    "Ett personuppgiftsbiträdesavtal behöver avtalstextens datum, det som står tryckt på pdf:en. Välj det i formuläret. Ingenting publicerades.",
  PORTAL_TEXT_EDITION_ONLY_FOR_PROCESSOR_AGREEMENT: "Avtalstextens datum hör bara till personuppgiftsbiträdesavtal. Ingenting publicerades.",
  // Build round 38, 2026-10-11: Search Console. Without these a refusal of a property reached the box
  // as PORTAL_UNEXPECTED, "Det gick inte", which the round's browser spec caught.
  PORTAL_INVALID_SEARCH_PROPERTY:
    "Egenskapen ska skrivas sc-domain:exempel.se eller https://www.exempel.se/ med snedstreck sist. Ingenting sparades.",
  PORTAL_SEARCH_PROPERTY_NOT_THE_SERVICES: "Egenskapen hör inte till tjänstens egen adress. Ingenting sparades.",
  PORTAL_UNKNOWN_SEARCH_PROPERTY: "Tjänsten har ingen egenskap sparad. Ladda om sidan.",
  PORTAL_GOOGLE_NOT_CONNECTED: "Google är inte kopplat. Koppla Google under Inställningar först.",
  PORTAL_INVALID_GOOGLE_STATE: "Google-kopplingen gick ut. Ingenting sparades. Försök igen.",
  // The only entry in this table the client decides for itself. Every other code was chosen by a
  // gateway and travelled here in an answer; this one covers the request never arriving anywhere to
  // be given a code. It says what did not happen, because the failure this replaces said "Något gick
  // fel. Försök igen." to somebody with no network, and trying again is the one thing that cannot
  // work.
  PORTAL_OFFLINE: "Ingen anslutning. Ingenting skickades. Försök igen när nätet är tillbaka."
};

/** Anything the server did not name. The interface never guesses at a cause. */
export const PORTAL_UNEXPECTED = "PORTAL_UNEXPECTED";

/**
 * The request never reached an answer. Exported so a screen can treat it as "wait" rather than as
 * "this failed", the way `isRateLimited` separates its own case below.
 */
export const PORTAL_OFFLINE = "PORTAL_OFFLINE";

const KNOWN_CODE = /PORTAL_[A-Z_]+/;

export interface PortalFailure {
  code: string;
  message: string;
}

/**
 * Reads the machine code out of a PostgREST/PostgREST-RPC failure.
 *
 * The gateways raise with `errcode` set and the code as the exception message, so the code arrives
 * in `message`. Anything unrecognised collapses to one unexpected-error code: a portal that echoes
 * a raw database string back to a client is leaking its own internals.
 *
 * The order below is the design. A code the server sent wins, always, because it is the one thing
 * here that is a fact rather than an inference: a 403 that arrived is a statement about access and
 * must not be overwritten by a guess about the network, even if the connection dropped one
 * millisecond later. Only a failure carrying no code at all is asked the connection question, and
 * then in two steps. A browser that says it is offline settles it, because an offline browser gives
 * up at whichever layer it likes and the resulting error has no reliable shape. Otherwise the shape
 * decides, which catches the case the browser cannot see: an interface that is up with nothing
 * reachable behind it.
 */
export function toPortalFailure(error: unknown): PortalFailure {
  const candidate = error as { message?: unknown; code?: unknown } | null;

  const rawMessage = typeof candidate?.message === "string" ? candidate.message : "";
  const match = KNOWN_CODE.exec(rawMessage);
  const named = match && PORTAL_ERROR_MESSAGES[match[0]] ? match[0] : null;
  const code =
    named ??
    (browserIsOffline() || isTransportFailure(error) ? PORTAL_OFFLINE : PORTAL_UNEXPECTED);

  return {
    code,
    message: PORTAL_ERROR_MESSAGES[code] ?? "Något gick fel. Försök igen."
  };
}

/**
 * A rate-limit refusal reaches the client as its own gateway code. It is separated here because
 * the interface treats it as "try later" rather than "you may not".
 */
export function isRateLimited(failure: PortalFailure): boolean {
  return failure.code === "PORTAL_RATE_LIMITED";
}
